import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, Download, ImageOff, Loader2, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import client, { unwrap, apiErrorMessage } from "@/api/client";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import ImportProgress from "@/components/catalogues/ImportProgress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const PRODUCT_TYPES = ["CURTAIN", "BLIND", "ROMAN_BLIND"];
const CATEGORIES = ["Upholstery", "Curtain", "Outdoor", "Multipurpose"];
const COLOUR_FAMILIES = ["Red", "Orange", "Yellow", "Green", "Blue", "Purple", "Pink", "Brown", "Beige", "Grey", "Black", "White", "Multi"];
const WORKING = ["QUEUED", "EXTRACTING", "PUBLISHING"];

const toNumber = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const toList = (v) => v.split(",").map((s) => s.trim()).filter(Boolean);
const orNull = (v) => (v === "" ? null : v);

const inputClass = "h-10 rounded-lg bg-card border-border text-sm placeholder:text-muted-foreground/70 focus-visible:ring-primary/20";

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[13px] font-normal text-foreground/75">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function TextField({ label, value, onChange, className, ...rest }) {
  return (
    <Field label={label} className={className}>
      <Input className={inputClass} value={value ?? ""} onChange={(e) => onChange(orNull(e.target.value))} {...rest} />
    </Field>
  );
}

function NumberField({ label, value, onChange, className, ...rest }) {
  return (
    <Field label={label} className={className}>
      <Input className={inputClass} type="number" min="0" step="any" value={value ?? ""} onChange={(e) => onChange(toNumber(e.target.value))} {...rest} />
    </Field>
  );
}

// Native select (keeps keyboard and mobile pickers), styled like the inputs.
function SelectField({ label, value, onChange, children, className, ...rest }) {
  return (
    <Field label={label} className={className}>
      <div className="relative">
        <select
          className="h-10 w-full appearance-none rounded-lg border border-border bg-card pl-3 pr-9 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          {...rest}
        >
          {children}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      </div>
    </Field>
  );
}

function Thumb({ url, alt, className = "" }) {
  return url ? (
    <img src={url} alt={alt} className={`object-cover bg-secondary ${className}`} />
  ) : (
    <div className={`flex items-center justify-center bg-secondary text-muted-foreground/60 ${className}`}>
      <ImageOff className="h-5 w-5" />
    </div>
  );
}

function SectionCard({ title, description, aside, children, className = "" }) {
  return (
    <section className={`bg-card border border-border rounded-2xl p-5 sm:p-6 ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-medium text-foreground">{title}</h2>
          {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

function ShadeCard({ shade, imageUrls, designName, onChange, onApplyPricesToAll, canApplyToAll, testId }) {
  const set = (key) => (value) => onChange({ [key]: value });
  const images = shade.imageFileIds?.length || 0;
  return (
    <div
      className={`rounded-xl border bg-card p-3.5 transition-opacity ${shade.include ? "border-border" : "border-dashed border-border opacity-60"}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <label className="inline-flex items-center gap-2 rounded-lg bg-secondary/70 px-2.5 py-1.5 text-sm text-foreground/80 cursor-pointer">
          <Checkbox checked={shade.include} onCheckedChange={(v) => onChange({ include: !!v })} data-testid={`${testId}-include`} />
          Include
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label={`Actions for shade ${shade.serialNumber}`} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary">
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onChange({ include: !shade.include })}>{shade.include ? "Exclude this shade" : "Include this shade"}</DropdownMenuItem>
            {canApplyToAll && (
              <DropdownMenuItem onSelect={onApplyPricesToAll}>Copy cost, MRP, HSN &amp; GST to all shades of this design</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[9rem_minmax(0,1fr)] gap-4">
        <div className="relative">
          <Thumb url={imageUrls[shade.imageFileIds?.[0]]} alt={`${designName} ${shade.serialNumber}`} className="w-full aspect-[4/3] sm:aspect-square rounded-lg" />
          {images > 1 && <span className="absolute bottom-1.5 right-1.5 rounded-full bg-black/55 px-2 py-0.5 text-[10px] text-white">{images} images</span>}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {shade.hex && <span className="h-4 w-4 rounded-full border border-black/10 shrink-0" style={{ backgroundColor: shade.hex }} title={shade.hex} />}
            <p className="text-xs text-muted-foreground truncate">
              SR {shade.serialNumber}
              {shade.page ? ` · P. ${shade.page}` : ""}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
            <TextField label="Serial no." value={shade.serialNumber} onChange={(v) => onChange({ serialNumber: v || "" })} />
            <TextField label="Shade code" value={shade.shadeCode} onChange={set("shadeCode")} />
            <TextField label="Colour name" value={shade.colourName} onChange={set("colourName")} />
            <SelectField label="Colour family" value={shade.colourFamily || ""} onChange={(v) => onChange({ colourFamily: orNull(v) })}>
              <option value="">—</option>
              {COLOUR_FAMILIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </SelectField>
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2.5">
        <NumberField label="Cost / m (₹)" value={shade.costPerMetre} onChange={set("costPerMetre")} placeholder="Enter cost" />
        <NumberField label="MRP / m (₹)" value={shade.sellingPricePerMetre} onChange={set("sellingPricePerMetre")} placeholder="Enter MRP" />
        <TextField label="HSN code" value={shade.hsnCode} onChange={set("hsnCode")} placeholder="Enter HSN" />
        <NumberField label="GST %" value={shade.gstPercent} onChange={set("gstPercent")} placeholder="Enter GST" />
      </div>
    </div>
  );
}

function DesignPanel({ design, di, imageUrls, onDesign, onShade }) {
  const set = (key) => (value) => onDesign({ [key]: value });
  const included = design.shades.filter((s) => s.include).length;
  const setAll = (include) => onDesign({ shades: design.shades.map((s) => ({ ...s, include })) });
  const applyPrices = (from) =>
    onDesign({
      shades: design.shades.map((s) => ({
        ...s,
        costPerMetre: from.costPerMetre,
        sellingPricePerMetre: from.sellingPricePerMetre,
        hsnCode: from.hsnCode,
        gstPercent: from.gstPercent,
      })),
    });

  return (
    <AccordionItem value={design.key} className="border border-border rounded-xl bg-secondary/20 mb-4 px-4 sm:px-5" data-testid={`import-design-${di}`}>
      <AccordionTrigger className="hover:no-underline py-4">
        <div className="flex items-center gap-3 text-left">
          <Thumb url={imageUrls[design.heroImageFileId]} alt={design.name} className="h-12 w-12 rounded-lg" />
          <div>
            <p className="font-serif text-lg text-foreground">
              {design.name}
              {design.designNumber && <span className="ml-2 font-sans text-xs font-mono-spec text-muted-foreground">#{design.designNumber}</span>}
            </p>
            <p className="text-xs text-muted-foreground">
              {included} of {design.shades.length} shades included
              {design.composition ? ` · ${design.composition}` : ""}
              {design.widthCm ? ` · ${design.widthCm} cm` : ""}
            </p>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <p className="text-sm font-medium text-foreground mb-3">Design details</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3.5 mb-6">
          <TextField label="Design name" value={design.name} onChange={(v) => onDesign({ name: v || "" })} />
          <TextField label="Design number" value={design.designNumber} onChange={set("designNumber")} />
          <SelectField label="Product type" value={design.productType || ""} onChange={(v) => onDesign({ productType: orNull(v) })}>
            <option value="">Catalogue default</option>
            {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </SelectField>
          <TextField label="Fabric type" value={design.fabricType} onChange={set("fabricType")} placeholder="boucle, velvet…" />
          <TextField label="Composition" value={design.composition} onChange={set("composition")} className="col-span-2" />
          <NumberField label="Width (cm)" value={design.widthCm} onChange={set("widthCm")} />
          <NumberField label="Weight (GSM)" value={design.weightGsm} onChange={set("weightGsm")} />
          <NumberField label="Martindale (rubs)" value={design.martindaleRubs} onChange={set("martindaleRubs")} />
          <TextField label="Pattern" value={design.pattern} onChange={set("pattern")} />
          <NumberField label="Repeat V (cm)" value={design.repeatVCm} onChange={set("repeatVCm")} />
          <NumberField label="Repeat H (cm)" value={design.repeatHCm} onChange={set("repeatHCm")} />
          <TextField label="Usage (comma separated)" value={(design.usage || []).join(", ")} onChange={(v) => onDesign({ usage: toList(v || "") })} className="col-span-2" />
          <TextField label="Features (comma separated)" value={(design.features || []).join(", ")} onChange={(v) => onDesign({ features: toList(v || "") })} className="col-span-2" />
          <TextField label="Wash & care" value={design.care} onChange={set("care")} className="col-span-2 md:col-span-4" />
        </div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-foreground">
            Shades <span className="text-muted-foreground font-normal">({design.shades.length})</span>
          </p>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => setAll(true)}>Include all</Button>
            <Button size="sm" variant="ghost" onClick={() => setAll(false)}>Exclude all</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 pb-3">
          {design.shades.map((shade, si) => (
            <ShadeCard
              key={shade.key}
              shade={shade}
              imageUrls={imageUrls}
              designName={design.name}
              testId={`import-shade-${di}-${si}`}
              onChange={(patch) => onShade(si, patch)}
              canApplyToAll={design.shades.length > 1}
              onApplyPricesToAll={() => applyPrices(shade)}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export default function CatalogueImportPage() {
  const { importId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);

  const { data: record, isLoading } = useQuery({
    queryKey: ["catalogue-import", importId],
    queryFn: () => client.get(`/catalogues/imports/${importId}`).then(unwrap),
    refetchInterval: (query) => (WORKING.includes(query.state.data?.status) ? 2000 : false),
  });

  // Take the server draft whenever it changes, unless the user has unsaved edits.
  useEffect(() => {
    if (record?.status === "REVIEW" && record.draft && !dirty) setDraft(record.draft);
  }, [record, dirty]);

  const imageUrls = record?.imageUrls || {};
  const edit = (fn) => {
    setDraft((d) => fn(d));
    setDirty(true);
  };
  const setMeta = (patch) => edit((d) => ({ ...d, catalogue: { ...d.catalogue, ...patch } }));
  const setDesign = (di, patch) => edit((d) => ({ ...d, designs: d.designs.map((x, i) => (i === di ? { ...x, ...patch } : x)) }));
  const setShade = (di, si, patch) =>
    edit((d) => ({
      ...d,
      designs: d.designs.map((x, i) => (i === di ? { ...x, shades: x.shades.map((s, j) => (j === si ? { ...s, ...patch } : s)) } : x)),
    }));

  const setAllShades = (include) => edit((d) => ({ ...d, designs: d.designs.map((x) => ({ ...x, shades: x.shades.map((sh) => ({ ...sh, include })) })) }));

  const counts = useMemo(() => {
    const designs = draft?.designs || [];
    const shades = designs.flatMap((d) => d.shades);
    return { designs: designs.length, shades: shades.length, included: shades.filter((s) => s.include).length };
  }, [draft]);

  const refresh = (data) => {
    if (data) queryClient.setQueryData(["catalogue-import", importId], data);
    queryClient.invalidateQueries({ queryKey: ["catalogue-imports"] });
  };

  const save = useMutation({
    mutationFn: () => client.patch(`/catalogues/imports/${importId}/draft`, draft).then(unwrap),
    onSuccess: (data) => {
      setDirty(false);
      refresh(data);
      toast.success("Draft saved");
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not save the draft")),
  });

  const publish = useMutation({
    mutationFn: async () => {
      if (dirty) {
        await client.patch(`/catalogues/imports/${importId}/draft`, draft);
        setDirty(false);
      }
      return client.post(`/catalogues/imports/${importId}/publish`).then(unwrap);
    },
    onSuccess: (res) => {
      refresh();
      queryClient.invalidateQueries({ queryKey: ["catalogues"] });
      toast.success(`Published ${res.productCount} products. The catalogue now awaits platform approval.`);
      navigate(`/catalogues/${res.catalogue._id}`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not publish")),
  });

  const retry = useMutation({
    mutationFn: () => client.post(`/catalogues/imports/${importId}/retry`).then(unwrap),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["catalogue-import", importId] }),
    onError: (err) => toast.error(apiErrorMessage(err, "Could not retry")),
  });

  const discard = useMutation({
    mutationFn: () => client.delete(`/catalogues/imports/${importId}`).then(unwrap),
    onSuccess: () => {
      refresh();
      toast.success("Import discarded");
      navigate("/catalogues");
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not discard")),
  });

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(record?.companyName || "catalogue").replace(/\W+/g, "_")}-${(record?.versionName || "draft").replace(/\W+/g, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const discardButton = (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="bg-card border-danger/40 text-danger hover:bg-danger/5 hover:text-danger" data-testid="import-discard-button">Discard</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard this import?</AlertDialogTitle>
          <AlertDialogDescription>The extracted draft will be dropped. Nothing has been published yet.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction className="bg-red-700 hover:bg-red-800" onClick={() => discard.mutate()}>Discard</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const progress = record?.progress?.total ? Math.round((record.progress.done / record.progress.total) * 100) : 5;

  return (
    <div data-testid="catalogue-import-page">
      <Link to="/catalogues" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-accent transition-colors mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Catalogues
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-semibold tracking-widest text-muted-foreground uppercase mb-1.5">PDF import</p>
          <h1 className="font-serif text-3xl sm:text-[2.6rem] leading-tight text-foreground">{record ? `${record.companyName} — ${record.versionName}` : "Loading…"}</h1>
          {record?.draft?.extraction && (
            <p className="text-sm text-[#6E6259] mt-1">
              {record.draft.extraction.pageCount} pages · extracted with {record.draft.extraction.method}
            </p>
          )}
        </div>
        {record && <StatusBadge status={record.status} />}
      </div>

      {isLoading && <p className="text-sm text-[#8C8379]">Loading…</p>}

      {(record?.status === "QUEUED" || record?.status === "EXTRACTING") && <ImportProgress record={record} />}

      {record?.status === "PUBLISHING" && (
        <div className="max-w-xl flex items-center gap-3 bg-card border border-border rounded-2xl p-5 text-sm text-foreground/85">
          <Loader2 className="h-4 w-4 animate-spin text-accent" /> Publishing products to your catalogue…
        </div>
      )}

      {record?.status === "FAILED" && (
        <div className="max-w-xl bg-red-50 border border-red-200 rounded-xl p-6" data-testid="import-failed">
          <p className="font-heading font-semibold text-red-800 mb-1">Extraction failed</p>
          <p className="text-sm text-red-700 mb-4 break-words">{record.error}</p>
          <div className="flex gap-3">
            <Button className="bg-[#C85A32] hover:bg-[#B04D2A] text-white" disabled={retry.isPending} onClick={() => retry.mutate()} data-testid="import-retry-button">
              Retry
            </Button>
            {discardButton}
          </div>
        </div>
      )}

      {record?.status === "PUBLISHED" && (
        <div className="max-w-xl bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <p className="text-sm text-emerald-800 mb-3">This import was published and is awaiting (or has passed) platform approval.</p>
          <Link className="text-sm font-medium text-[#C85A32] hover:underline" to={`/catalogues/${record.publishedCatalogueId}`}>View catalogue →</Link>
        </div>
      )}

      {record?.status === "DISCARDED" && <p className="text-sm text-[#6E6259]">This import was discarded.</p>}

      {record?.status === "REVIEW" && draft && (
        <Tabs defaultValue="review">
          <TabsList className="mb-5 bg-secondary">
            <TabsTrigger value="review">Review</TabsTrigger>
            <TabsTrigger value="json" data-testid="import-json-tab">View JSON</TabsTrigger>
          </TabsList>

          <TabsContent value="review" className="space-y-6">
            <SectionCard title="Catalogue Details" description="This information will be used to organise this catalogue in your system.">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
                <TextField label="Company name" value={draft.catalogue.companyName} onChange={(v) => setMeta({ companyName: v || "" })} />
                <TextField label="Catalogue / version" value={draft.catalogue.versionName} onChange={(v) => setMeta({ versionName: v || "" })} />
                <TextField label="Brand" value={draft.catalogue.brand} onChange={(v) => setMeta({ brand: v })} placeholder="Enter brand (optional)" />
                <SelectField label="Category" value={draft.catalogue.category || ""} onChange={(v) => setMeta({ category: orNull(v) })}>
                  <option value="">—</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </SelectField>
                <SelectField label="Default product type" value={draft.catalogue.defaultProductType} onChange={(v) => setMeta({ defaultProductType: v })}>
                  {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </SelectField>
                <TextField label="Website" value={draft.catalogue.website} onChange={(v) => setMeta({ website: v })} placeholder="https://www.example.in" />
                <TextField label="Year / season" value={draft.catalogue.season} onChange={(v) => setMeta({ season: v })} placeholder="e.g. 2026 / Summer" />
                <Field label="Description" className="sm:col-span-2 lg:col-span-4">
                  <Textarea
                    rows={2}
                    className="rounded-lg bg-card border-border text-sm placeholder:text-muted-foreground/70"
                    value={draft.catalogue.description || ""}
                    onChange={(e) => setMeta({ description: orNull(e.target.value) })}
                  />
                </Field>
              </div>
            </SectionCard>

            <SectionCard
              title="Shades"
              description={`These shades will be published as products. Select the ones you want to include.${counts.designs > 1 ? ` ${counts.designs} designs found.` : ""}`}
              aside={
                counts.shades > 0 && (
                  <div className="flex items-center gap-5 text-sm text-foreground/85">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      Include all
                      <Checkbox data-testid="import-include-all" checked={counts.included === counts.shades} onCheckedChange={() => setAllShades(true)} />
                    </label>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      Exclude all
                      <Checkbox data-testid="import-exclude-all" checked={counts.included === 0} onCheckedChange={() => setAllShades(false)} />
                    </label>
                  </div>
                )
              }
            >
              {counts.designs === 0 && (
                <p className="text-sm text-muted-foreground">No designs were found in this PDF. Discard it, or retry with a different file.</p>
              )}
              <Accordion type="multiple" defaultValue={draft.designs.slice(0, 1).map((d) => d.key)}>
                {draft.designs.map((design, di) => (
                  <DesignPanel
                    key={design.key}
                    design={design}
                    di={di}
                    imageUrls={imageUrls}
                    onDesign={(patch) => setDesign(di, patch)}
                    onShade={(si, patch) => setShade(di, si, patch)}
                  />
                ))}
              </Accordion>
            </SectionCard>
          </TabsContent>

          <TabsContent value="json">
            <div className="flex justify-end mb-2">
              <Button variant="outline" size="sm" onClick={downloadJson}><Download className="h-3.5 w-3.5 mr-1.5" /> Download JSON</Button>
            </div>
            <pre className="bg-[#2A211D] text-[#F4EFEA] text-xs rounded-xl p-4 overflow-auto max-h-[70vh]" data-testid="import-json">
              {JSON.stringify(draft, null, 2)}
            </pre>
          </TabsContent>

          <div className="sticky bottom-16 md:bottom-0 z-20 mt-6 -mx-4 md:-mx-6 lg:-mx-8 border-t border-border bg-background/95 backdrop-blur-sm">
            <div className="px-4 md:px-6 lg:px-8 py-3.5 flex flex-wrap items-center gap-3">
              <p className="text-sm text-foreground/85 mr-auto" data-testid="import-counts">
                {counts.included} {counts.included === 1 ? "shade" : "shades"} selected
                <span className="text-muted-foreground"> · They will be published as products</span>
                {dirty && <span className="ml-2 text-xs text-accent">· Unsaved changes</span>}
              </p>
              {discardButton}
              <Button variant="outline" className="bg-card" disabled={!dirty || save.isPending} onClick={() => save.mutate()} data-testid="import-save-button">
                {save.isPending ? "Saving…" : "Save draft"}
              </Button>
              <Button
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
                disabled={!counts.included || publish.isPending}
                onClick={() => publish.mutate()}
                data-testid="import-publish-button"
              >
                {publish.isPending ? "Publishing…" : `Publish ${counts.included} ${counts.included === 1 ? "product" : "products"}`}
              </Button>
            </div>
          </div>
        </Tabs>
      )}
    </div>
  );
}
